import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import axios from 'axios'
import fileDownload from 'js-file-download'
import {jwt} from "../mixins/AuthMixin"

Vue.use(Vuex)

// Prettier stringifier
export const str = obj => typeof obj === 'string' || typeof obj === 'number'
  ? obj + ''
  : JSON.stringify(obj, null, 2)

// String compare
export const strCmp = (str1, str2) => str1 > str2 ? 1 : str1 === str2 ? 0 : -1

// Loads configs from .env file
export const env = (key, defaultValue) => process.env['VUE_APP_' + key] || defaultValue

// Adds host prefix to a route
export const host = suffix => {
  const prefix = Store.state.env + '_'
  let url = env(prefix + 'SERVER_HOST')
  const port = env(prefix + 'SERVER_PORT')
  if (port && port.trim() !== '')
    url += ':' + port

  return url + suffix
}
export const api = route => host('/api/dashboard' + route)

// Adds assets root prefix to an asset path
export const asset = (path, _default) => path ? host(path) : _default

export const fetchAsBase64 = url => new Promise((resolve, reject) => {
  axios.get(url, {responseType: 'arraybuffer'})
    .then(res => {
      const image = new Buffer(res.data, 'binary').toString('base64')
      const result = `data:${res.headers['content-type'].toLowerCase()};base64,${image}`

      resolve(result)
    })
    .catch(e => {
      reject(e)
    })
})

export const fetchAsBase64Sync = async url => await fetchAsBase64(url)

export const price = (p) => {
  if (!p && p !== 0)
    return ''

  const company = Store.state.company
  if (company && company.currency)
    p += ' ' + company.currency.name

  return p
}


export const Api = {
  catchErrors(promise) {
    return new Promise((resolve, reject) => {
      promise
        .then(res => {
          if (res.status === "success" && !res.message)
            resolve(res)
          else
            reject(res)
        })
        .catch(reject)
    })
  },
  get(route, params, config) {
    return this.catchErrors(
      axios.get(api(route), {
        params,
        ...jwt(config)
      })
        .then(res => res.data)
    )
  },
  post(route, data, config) {
    return this.catchErrors(axios.post(api(route), data, jwt(config)).then(res => res.data))
  },
  postFormData(route, data, config) {
    if (!config)
      config = {}

    if (!config.headers)
      config.headers = {}

    config.headers['Content-Type'] = 'multipart/form-data'

    return this.post(route, new _FormData(data), config)
  },
  download(route, params, config) {
    return new Promise((resolve, reject) => {
      axios.get(api(route), {params, responseType: "blob", ...jwt(config)})
        .then(res => {
          const filename = res.headers['content-disposition'].split('filename=')[1]
          fileDownload(res.data, filename)
          resolve(res)
        })
        .catch(reject)
    })
  }
}

export const Storage = {
  store(key, value) {
    return localStorage.setItem(key, JSON.stringify(value))
  },
  get(key, defaultValue) {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item)
      : defaultValue ? defaultValue : item
  },
  remove(key) {
    return localStorage.removeItem(key)
  }
}

export const Store = new Vuex.Store({
  state: {
    env: env('DEFAULT_SERVER'),
  },
  mutations: {
    setEnv(state, env) {
      state.env = env
    },
  },
  plugins: [createPersistedState()]
})

export const Cache = {
  cacheKey: '__emsit_cache__',
  get(key) {
    const cache = Storage.get(this.cacheKey)
    if (cache)
      return cache[key]
  },
  store(key, value) {
    const cache = Storage.get(this.cacheKey, {})
    cache[key] = value
    Storage.store(this.cacheKey, cache)
  }
}

export const Regex = {
  isEmpty(str) {
    return str == null || str === ''
  },
  isNotEmpty(str) {
    return !this.isEmpty(str)
  },
  isBlank(str) {
    return this.isEmpty(str) || !!str.match(/^\s*$/)
  },
  isNotBlank(str) {
    return !this.isBlank(str)
  },
  isEmail(str) {
    return !this.isEmpty(str) && !!str.match(/^[\w.]+@[\w\-]+\.[a-z]{2,12}$/i)
  },
  isValidEmailList(str) {
    return !this.isEmpty(str) && str.split(',').every(part => this.isEmail(part))
  },
  isEmptyOrEmailList(str) {
    return this.isEmpty(str) || this.isValidEmailList(str)
  },
  isEmptyOrEmail(str) {
    return this.isEmpty(str) || this.isEmail(str)
  },
  isPassword(str) {
    return !this.isEmpty(str) && str.length >= 8
      && str.match(/\d+/) && str.match(/[a-zA-Z]+/) && str.match(/[^a-zA-Z\d]+/)
  },
  isEmptyOrPassword(str) {
    return this.isEmpty(str) || this.isPassword(str)
  },
  isPhoneNumber(str) {
    return !this.isEmpty(str) && !!str.match(/^\+?[\d ]{6,50}$/i)
  },
  isEmptyOrPhoneNumber(str) {
    return this.isEmpty(str) || this.isPhoneNumber(str)
  },
  isCMRPhoneNumber(str) {
    if (this.isEmpty(str)) return false

    str = str.replace(/ /g, '')
    return str.indexOf('+237') === 0 || str.indexOf('00237') === 0
  },
  isNumber(str) {
    return !this.isEmpty(str) && !!(str + '').match(/^[+\-]?\d+([.,]\d+)?$/i)
  },
  isPositiveNumber(str) {
    return this.isNumber(str) && parseFloat(str) >= 0
  },
  isValidRate(str) {
    return this.isPositiveNumber(str) && parseFloat(str) <= 100
  },
  isStrictPositiveNumber(str) {
    return this.isNumber(str) && parseFloat(str) > 0
  },
  isInteger(str) {
    return !this.isEmpty(str) && !!(str + '').match(/^[+\-]?\d+$/i)
  },
  isPositiveInteger(str) {
    return this.isInteger(str) && parseInt(str) >= 0
  },
  isStrictPositiveInteger(str) {
    return this.isInteger(str) && parseInt(str) > 0
  },
  isEmptyOrPositiveNumber(str) {
    return this.isEmpty(str) || this.isPositiveNumber(str)
  },
  isEmptyOrStrictPositiveInteger(str) {
    return this.isEmpty(str) || this.isStrictPositiveInteger(str)
  },
  isValidUserCode(str) {
    return this.isPositiveNumber(str)
  },
  isValidPhoneConfirmationCode(str) {
    return this.isPositiveNumber(str) && (str + '').length === 6
  },
  isURL(str) {
    return !this.isEmpty(str) && str.match(/^http(s)?:\/\//i)
  },
  isNullOrURL(str) {
    return this.isEmpty(str) || this.isURL(str)
  },
  isValidIDNumber(str) {
    return !this.isEmpty(str)
  },
  isDatetime: function (str) {
    return !this.isEmpty(str) && !!str.match(/^\d{1,4}[-/]\d{1,4}[-/]\d{1,4} \d{1,2}(:\d{1,2}){1,2}$/)
  },
  isDate(str) {
    return !this.isEmpty(str) && !!str.match(/^\d{1,4}[-/]\d{1,4}[-/]\d{1,4}$/)
  },
  isValidIp(str) {
    return str === 'xxx.xxx.xxx.xxx' || (!this.isEmpty(str)
      && !!str.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)
      && str.split('.').every(part => parseInt(part) <= 255)
    )
  },
  isValidIpList(str) {
    return !this.isEmpty(str) && str.split(',').every(part => this.isValidIp(part))
  },
  isValidFilename(str) {
    return !this.isEmpty(str) && !!str.match(/^[\w.-]+$/i)
  },
  isValidCertificate(str) {
    return !this.isEmpty(str)
      && !!str.match(/^-----BEGIN CERTIFICATE-----[a-zA-Z/+\d\s]+=\s+-----END CERTIFICATE-----$/i)
  },
  isEmptyOrValidCertificate(str) {
    return this.isEmpty(str) || this.isValidCertificate(str)
  },
}

export const String = {
  hiddenPhoneNumber(str) {
    if (!str)
      return str

    str = str.replace(/ /g, '')
    return str.slice(0, -4).replace(/./g, '*') + str.slice(-4)
  },
  toNDigits(number, n) {
    for (let i = 0; i < n; i++)
      number = '0' + number

    return number.slice(-n)
  }
}

export const Toast = {
  options: {
    duration: 3500,
    position: 'top-center',
    theme: 'bubble',
    iconPack: 'fontawesome',
    action: {
      text: 'Fermer',
      onClick: (e, toastObject) => {
        toastObject.goAway(0);
      }
    }
  },
  loadingToast: {
    visible: false,
    toast: null,
    goAway(delay) {
      if (this.toast)
        this.toast.goAway(delay || 100)
      this.visible = false
    }
  },
  _(msg) {
    return !msg
      ? '---'
      : typeof msg === 'string'
        ? msg
        : msg.message;
  },
  getOptions(icon, duration) {
    const options = {...this.options}
    if (icon)
      options.icon = icon

    if (duration)
      options.duration = duration

    return options
  },
  show(msg, duration) {
    return Vue.toasted.show(this._(msg), this.getOptions(null, duration))
  },
  success(msg, duration) {
    return Vue.toasted.success(this._(msg), this.getOptions('check', duration))
  },
  info(msg, duration) {
    return Vue.toasted.info(this._(msg), this.getOptions('info', duration))
  },
  error(msg, duration) {
    return Vue.toasted.error(this._(msg), this.getOptions('remove', duration))
  },
  loading(msg) {
    if (!this.loadingToast.visible) {
      this.loadingToast.toast = Vue.toasted.show(msg || 'Chargement...', {
        duration: 5 * 60 * 1000, // 5 mins
        position: 'top-center',
        iconPack: 'fontawesome',
        icon: 'hourglass'
      })
      this.loadingToast.visible = true
    }

    return this.loadingToast
  }
}

export const DateFormatter = {
  forFilename(date) {
    if (!date)
      date = new Date()

    const y = date.getFullYear()
    const m = ('0' + (date.getMonth() + 1)).slice(-2)
    const d = ('0' + date.getDate()).slice(-2)

    const h = ('0' + date.getHours()).slice(-2)
    const i = ('0' + date.getMinutes()).slice(-2)
    const s = ('0' + date.getSeconds()).slice(-2)

    return `${y}-${m}-${d}_${h}h${i}min${s}s`
  },
  raw(date) {
    return this.forFilename(date).replace(/\D+/g, '')
  },
  toSimpleDatetime(date) {
    if (!date)
      date = new Date()

    const y = date.getFullYear()
    const m = ('0' + (date.getMonth() + 1)).slice(-2)
    const d = ('0' + date.getDate()).slice(-2)

    const h = ('0' + date.getHours()).slice(-2)
    const i = ('0' + date.getMinutes()).slice(-2)

    return `${d}/${m}/${y} à ${h}:${i}`
  },
  toSimpleDateEn(date) {
    if (!date)
      date = new Date()

    const y = date.getFullYear()
    const m = ('0' + (date.getMonth() + 1)).slice(-2)
    const d = ('0' + date.getDate()).slice(-2)

    return `${y}-${m}-${d}`
  },
  toSimpleDatetimeEn(date) {
    if (!date)
      date = new Date()

    const h = ('0' + date.getHours()).slice(-2)
    const i = ('0' + date.getMinutes()).slice(-2)
    const s = ('0' + date.getSeconds()).slice(-2)

    return this.toSimpleDateEn(date) + ` ${h}:${i}:${s}`
  },
  todayStart() {
    const date = new Date()
    date.setUTCDate(date.getDate() - 1)
    date.setUTCHours(23, 0, 0, 0)

    return this.toSimpleDatetimeEn(date)
  },
  todayEnd() {
    const date = new Date()
    date.setUTCHours(22, 59, 59, 999)

    return this.toSimpleDatetimeEn(date)
  },
  frToEnDatetime(datetime) {
    datetime += ''
    const parts = datetime.split(' ')
    let enDatetime = parts[0].split('/').reverse().join('-') + ' '
    if (parts.length >= 2)
      enDatetime += parts[1]

    return enDatetime
  },
  enToFrDatetime(datetime) {
    datetime += ''
    const parts = datetime.split(' ')
    let enDatetime = parts[0].split('-').reverse().join('/') + ' '
    if (parts.length >= 2)
      enDatetime += parts[1]

    return enDatetime
  }
}

export class _FormData extends FormData {
  constructor(data) {
    super()
    for (let key in data) {
      if (data.hasOwnProperty(key))
        this.append(key, data[key])
    }
  }
}

export class _Date extends Date {
  constructor(str) {
    super(DateFormatter.frToEnDatetime(str))
  }

  /**
   * @param hours string
   * @return _Date
   */
  addHours(hours) {
    this.setHours(this.getHours() + parseInt(hours))
    return this
  }

  /**
   * @return string
   */
  toStringFr() {
    return DateFormatter.toSimpleDatetime(this)
  }
}

//http://phpjs.org/functions/chunk_split:369
function chunk_split(body, chunklen, end) {
  // Returns split line
  //
  // version: 1103.1210
  // discuss at: http://phpjs.org/functions/chunk_split
  // +   original by: Paulo Freitas
  // +      input by: Brett Zamir (http://brett-zamir.me)
  // +   bugfixed by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  // +   improved by: Theriault
  // *     example 1: chunk_split('Hello world!', 1, '*');
  // *     returns 1: 'H*e*l*l*o* *w*o*r*l*d*!*'
  // *     example 2: chunk_split('Hello world!', 10, '*');
  // *     returns 2: 'Hello worl*d!*'
  chunklen = parseInt(chunklen, 10) || 76;
  end = end || '\r\n';

  if (chunklen < 1) {
    return false;
  }

  return body.match(new RegExp(".{0," + chunklen + "}", "g")).join(end);
}

const strrev = str => str.split("").reverse().join("")
const ltrim = (str, char = ' ') => str.replace(new RegExp(`^${char}+`, "g"), '')
const rtrim = (str, char = ' ') => str.replace(new RegExp(`${char}+$`, "g"), '')

export const formatAmount = amount => {
  if (!amount)
    return amount

  amount = parseFloat(amount)

  if (amount === 0)
    return 0

  if (amount < 0)
    return '-' + formatAmount(-amount)

  const parts = (amount + '').split('.')
  amount = parseInt(parts[0]) && strrev(chunk_split(strrev(ltrim(parts[0], '0')), 3, ' ')).trim()

  if (parts.length > 1 && parseInt(parts[1]) !== 0)
    amount += ',' + rtrim(parts[1], '0')

  return amount.trim()
}

const ceil = (value, precision = 0) => {
  if (precision <= 0) return Math.ceil(value);

  const pow = Math.pow(10, precision);
  value *= pow;
  const ceil = Math.ceil(value);
  return (ceil + Math.ceil(value - ceil)) / pow;
}

export const formatPercent = (value, total, precision = 2) => {
  if (total === 0)
    return '0 %'

  return ceil(100.0 * value / total, precision) + ' %'
}

export const truncate = (str, max) => {
  if (!str || str.length <= max)
    return str

  return str.substr(0, max - 2) + '...'
}

export const truncateLeft = (str, max) => {
  if (!str)
    return str

  return strrev(truncate(strrev(str), max))
}

export const getFilename = path => {
  if (!path)
    return path

  return path.replace(/^.*[\\\/]/, '')
}

export const mergeIf = (cond, trueItems, falseItems) => {
  if (cond)
    return trueItems.constructor === [].constructor ? trueItems : [trueItems]

  if (falseItems == null)
    return []

  return falseItems.constructor === [].constructor ? falseItems : [falseItems]
}

export const mergeObjectIf = (cond, trueObj, falseObj) => cond ? trueObj : falseObj || {}
